import { getFormValues } from 'redux-form';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { push } from 'redux-first-history';
import differenceInDays from 'date-fns/differenceInDays';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import api from '../../../core/api';
import { composeErrorMessage, transformFormValueToNumber } from '../../../core/helpers/utils/utils';
import { selectActions } from '../../actions';
import { previewEditFeedGroup } from '../../application/groupPreview/groupPreview.actions';
import { resetFeedingLoads } from '../../application/loadMixers/loadMixers.actions';
import { getFeedingLoadsSaga } from '../../application/loadMixers/loadMixers.saga';
import * as loadMixersSelector from '../../application/loadMixers/loadMixers.selector';
import { Forms } from '../../form/form.selector';
import {
  getSelectedBunkReaderPen,
  getSelectedBunkReaderPenId,
  getSelectedGroup,
} from '../../selected/selected.selector';
import { LoadersNames } from '../../ui/ui.constants';
import { getFarmsListRequest } from '../farms/farms.actions';
import { getFeedingPeriodList } from '../feeding/feeding.actions';
import * as actions from './group.actions';
import { BunkReaderFormValues, Group } from './group.types';
import { feedGroupsPaths } from '../../../routes/FeedGroups/feedGroupsPaths';
import { farmsSelector, settingsSelector } from '../../selectors';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { BunkScoresPayload, BunkScoresStatus } from '../settings/settings.types';
import { getBunkScoreGrades, getTimeZone } from '../settings/settings.selector';
import { getUserProfile } from '../../user/user.selector';
import { getDaysBack } from '../../../shared/DateRangePicker/utils';

export function* getGroupListSaga() {
  yield put(showLoader(LoadersNames.fetchGroupList));
  try {
    const result = yield call(api.group.getGroupList);
    yield put(
      actions.getGroupList.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchGroupList));
}

export function* getAllGroupsSaga() {
  yield put(showLoader(LoadersNames.fetchGroupList));
  try {
    const result = yield call(api.group.getGroupList, true, true);
    yield put(
      actions.getAllGroups.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchGroupList));
}

export function* getGroupDetailByIdSaga(action: Action<{ groupId: number }>) {
  yield put(showLoader(LoadersNames.fetchGroup));
  try {
    const result = yield call(api.group.getGroupById, action.payload.groupId);
    yield put(
      actions.getGroupById.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchGroup));
}

export function* addGroupSaga(action: ReturnType<typeof actions.addGroup.started>) {
  yield put(showLoader(LoadersNames.addGroup));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.group.addGroup, data, formName);
    yield put(
      actions.addGroup.done({
        params: action.payload,
        result,
      }),
    );

    yield all([
      yield put(notificationSuccess('feeds:add_group_success')),
      yield put(selectActions.setSelectedGroup(result.id)),
      yield put(push(feedGroupsPaths.groups)),
    ]);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.addGroup));
}

export function* editGroupSaga(action: ReturnType<typeof actions.editGroup.started>) {
  yield put(showLoader(LoadersNames.editGroup));

  try {
    const { data, formName, noSave } = action.payload;
    const meta = action.meta;
    const redirectPath = meta && meta.redirectPath ? meta.redirectPath : feedGroupsPaths.groups;
    const silentSuccess = meta && meta.silentSuccess;
    const withNoRedirect = meta && meta.withNoRedirect;
    const result = yield call(api.group.patchGroup, data, data.id!, formName, noSave);
    yield put(
      actions.editGroup.done({
        params: action.payload,
        result,
      }),
    );

    yield all([
      !withNoRedirect && (yield put(push(redirectPath))),
      !silentSuccess && (yield put(notificationSuccess('feeds:edit_group_success'))),
    ]);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.editGroup));
}

export function* editGroupsSaga(action: ReturnType<typeof actions.editGroups.started>) {
  yield put(showLoader(LoadersNames.editGroup));
  try {
    const { data, noSave, formName } = action.payload;
    const result = yield call(api.group.patchGroups, data, noSave, formName);

    yield put(
      actions.editGroups.done({
        params: action.payload,
        result,
      }),
    );
    successHandler(action.meta);
  } catch (err) {
    yield compose403ErrorMessage(err);
  } finally {
    yield put(hideLoader(LoadersNames.editGroup));
  }
}

export function* deleteGroupSaga(action: ReturnType<typeof actions.deleteGroup.started>) {
  const selectedGroup: Group = yield select(getSelectedGroup);
  const formName = action.payload;
  try {
    yield call(api.group.deleteGroup, selectedGroup.id, formName);
    yield put(
      actions.deleteGroup.done({
        params: formName,
        result: selectedGroup.id,
      }),
    );
    yield put(notificationSuccess('feeds:delete_group_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* updateGroupPropertiesSaga(action: ReturnType<typeof actions.updateGroup.started>) {
  const groupFormValues = yield select(getFormValues(Forms.groupEditForm));
  const selectedGroup: Group = yield select(getSelectedGroup);
  const hasSelectedFeedingPeriods = yield select(loadMixersSelector.hasSelectedFeedingPeriods);
  const hasAtLeastOneMixerSelected = yield select(loadMixersSelector.hasAtLeastOneMixerSelected);
  const isBeef = yield select(farmsSelector.isCurrentFarmBeef);
  const formName = action.payload;

  try {
    const group = {
      id: selectedGroup.id,
      numberOfCows: !isBeef ? groupFormValues.numberOfCows : undefined,
      globalFeedingFactor: groupFormValues.globalFeeding,
    };
    const result = yield call(api.group.patchGroup, group as any, selectedGroup.id, formName);
    yield put(
      actions.updateGroup.done({
        params: formName,
        result,
      }),
    );
    yield put(previewEditFeedGroup.started({ id: selectedGroup.id }));

    if (hasSelectedFeedingPeriods && hasAtLeastOneMixerSelected) {
      yield call(getFeedingLoadsSaga);
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* editAdditionalCostsInGroupSaga(action: ReturnType<typeof actions.editAdditionalCostsInGroup.started>) {
  yield put(showLoader(LoadersNames.updatePenAdditionalCosts));
  const formValues = yield select(getFormValues(Forms.costTypesEditorForm));
  const selectedGroup = yield select(getSelectedGroup);
  const timezone = yield select(settingsSelector.getTimeZone);
  try {
    const id = selectedGroup.id;

    const additionalCosts = Object.values<any>(formValues)
      .flat()
      .map(({ changed, error, appliedPer, chargePeriod, chargeType, startDateTime, endDateTime, ...rest }) => ({
        ...rest,
        startDateTime: toTimezoneDate(toLocalDate(new Date(startDateTime)), timezone),
        endDateTime: endDateTime ? toTimezoneDate(toLocalDate(new Date(endDateTime)), timezone) : undefined,
      })) as any;

    const result = yield call(api.group.patchGroup, { id, additionalCosts }, id, FormName.groupEdit);

    yield put(
      actions.updateGroup.done({
        params: FormName.groupEdit,
        result,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('lots:cost_type_added'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updatePenAdditionalCosts));
}

export function* deleteFeedingGroupSaga(action: Action<{ id: string; formName: any }>) {
  const { id: groupId } = yield select(getSelectedGroup);
  const { id: feedingId, formName } = action.payload;

  try {
    const result = yield call(api.group.deleteFeeding, groupId, feedingId, formName);
    yield put(resetFeedingLoads());
    yield put(getFeedingPeriodList.started({}));
    yield put(
      actions.deleteFeedingGroup.done({
        params: { feedingId: feedingId, groupId: groupId },
        result,
      }),
    );
    yield put(previewEditFeedGroup.started({ id: groupId }));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* deleteFeedingSaga(action: ReturnType<typeof actions.deleteFeeding.started>) {
  yield put(showLoader(LoadersNames.deleteFeeding));

  const { feedingId, groupId, formName } = action.payload;

  try {
    const result = yield call(api.group.deleteFeeding, groupId, feedingId, formName);
    yield put(
      actions.deleteFeeding.done({
        params: action.payload,
        result,
      }),
    );

    if (action.meta) {
      if (typeof action.meta.onSuccess === 'function') {
        action.meta.onSuccess();
      }
    }
    yield put(notificationSuccess('feeds:feeding_remove_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.deleteFeeding));
}

function* getBunkReaderGroupByIdSaga(action: ReturnType<typeof actions.getBunkReaderGroupById.started>) {
  yield put(showLoader(LoadersNames.fetchGroup));
  try {
    const formValues = yield select(getFormValues(Forms.bunkReaderDateRange));

    const timezone = yield select(getTimeZone);

    const { selectedTimeRange } = formValues || {};

    let daysBack: number | undefined;
    let endDate: Date | undefined;

    if (selectedTimeRange) {
      daysBack = getDaysBack(
        selectedTimeRange.labelKey,
        differenceInDays(selectedTimeRange.endDate, selectedTimeRange.startDate),
      );
      endDate = toTimezoneDate(toLocalDate(selectedTimeRange.endDate), timezone);
    }

    const result = yield call(api.group.getBunkReaderGroupById, action.payload, daysBack, endDate);

    yield put(
      actions.getBunkReaderGroupById.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchGroup));
}

function* addBunkScoresSaga(action: ReturnType<typeof actions.addBunkScores.started>) {
  yield put(showLoader(LoadersNames.addBunkReaderScore));
  try {
    const groupId: string = yield select(getSelectedBunkReaderPenId);
    const { id: userId } = yield select(getUserProfile);
    const formValues: BunkReaderFormValues = yield select(getFormValues(FormName.bunkreader));
    const bunkReader = yield select(getSelectedBunkReaderPen);
    const bunkScoreGrades = yield select(getBunkScoreGrades);

    const payload: BunkScoresPayload = {
      userId,
      timestamp: new Date().toISOString(),
      status: BunkScoresStatus.PROCESSED,
    };

    switch (action.payload) {
      case 'globalFeedingFactor':
        payload.resultPercentage =
          transformFormValueToNumber(formValues.globalFeedingFactor) - bunkReader.globalFeedingFactor;
        break;
      case 'actualWeightTotal':
        payload.resultWeight = transformFormValueToNumber(formValues.actualWeightTotal) - bunkReader.actualWeightTotal;
        break;
      case 'brDefaultscore':
        payload.gradeId = bunkScoreGrades.find(({ score }) => score === formValues.brDefaultscore).id;
        break;
      default:
        payload.gradeId = bunkScoreGrades.find(({ score }) => score === formValues.brDefaultscore).id;
    }

    yield call(api.group.addBunkScores, groupId, payload);

    // Updating a group will fire a notification to get all groups, that can overwrite GET single group call results
    // that is fired within successHandler(action.meta). We need to wait when GET all groups call is done and then fire GET single group

    // yield take(actions.getAllGroups.done);

    yield put(hideLoader(LoadersNames.addBunkReaderScore));

    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* addBunkReaderGroupNote(action: ReturnType<typeof actions.addBunkReaderGroupNote.started>) {
  try {
    const { groupId, note } = action.payload;
    const result = yield call(api.group.addBunkReaderNote, groupId, note);

    yield put(
      actions.addBunkReaderGroupNote.done({
        params: action.payload,
        result: { groupId, note: result },
      }),
    );
    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* editBunkReaderGroupNoteSaga(action: ReturnType<typeof actions.editBunkReaderGroupNote.started>) {
  try {
    const { groupId, note } = action.payload;
    const result = yield call(api.group.editBunkReaderNote, groupId, note);

    yield put(
      actions.editBunkReaderGroupNote.done({
        params: action.payload,
        result: { groupId, note: result },
      }),
    );
    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* deleteBunkReaderGroupNoteSaga(action: ReturnType<typeof actions.deleteBunkReaderGroupNote.started>) {
  try {
    const { groupId, noteId } = action.payload;
    yield call(api.group.deleteBunkReaderNote, groupId, noteId);

    yield put(
      actions.deleteBunkReaderGroupNote.done({
        params: action.payload,
        result: undefined,
      }),
    );

    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchGroupSaga() {
  yield all([
    takeLatest(actions.getGroupList.started, getGroupListSaga),
    takeLatest(actions.addGroup.started, addGroupSaga),
    takeLatest(actions.editGroup.started, editGroupSaga),
    takeLatest(actions.editGroups.started, editGroupsSaga),
    takeLatest(getFarmsListRequest.done, getAllGroupsSaga),
    takeLatest(actions.getAllGroups.started, getAllGroupsSaga),
    takeEvery(actions.getGroupById.started, getGroupDetailByIdSaga),
    takeLatest(actions.deleteGroup.started, deleteGroupSaga),
    takeLatest(actions.updateGroup.started, updateGroupPropertiesSaga),
    takeLatest(actions.deleteFeedingGroup.started, deleteFeedingGroupSaga),
    takeLatest(actions.deleteFeeding.started, deleteFeedingSaga),
    takeLatest(actions.editAdditionalCostsInGroup.started, editAdditionalCostsInGroupSaga),
    takeLatest(actions.getBunkReaderGroupById.started, getBunkReaderGroupByIdSaga),
    takeLatest(actions.addBunkScores.started, addBunkScoresSaga),
    takeLatest(actions.addBunkReaderGroupNote.started, addBunkReaderGroupNote),
    takeLatest(actions.editBunkReaderGroupNote.started, editBunkReaderGroupNoteSaga),
    takeLatest(actions.deleteBunkReaderGroupNote.started, deleteBunkReaderGroupNoteSaga),
  ]);
}
